import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { SkeletonUtils } from 'three-stdlib'
import { cloneAndScaleUV, setupTextureRepeat } from "../../utils/geometryUltis"
import useGateBoundingDataChecker from "../../utils/gateBoundingDataChecker"

const BramaSegmentowa = forwardRef(({ gate, onUpdate, textures, ...props }, ref) => {
  const group = useRef()
  const { scene, animations } = useGLTF('/models/gates/Brama-segmentowa.glb')
  const cloned = useRef()

  // Clone and UV scale once per change
  useEffect(() => {
    if (!scene) return

    const newClone = SkeletonUtils.clone(scene)
    const isTargetMaterial = (m) => !!m && m.name === 'Material'

    // ✅ globalny switch: WOOD = powtarzamy tekstury, INNE (tu: plastic) = rozciągamy 1:1
    const isWoodGlobal = textures?.gates_sectional_color?.materialType === 'wooden'

    // repeat z gc (standard)
    const applyRepeatFromGC = (t, gc) => {
      if (!t) return
      setupTextureRepeat(
        t,
        1 / gc.multiplierHorizontal,
        1 / gc.multiplierVertical
      )
    }

    // ✅ WOOD: obrót tekstury o 90° aby szła lewo->prawo + swap repeat X/Y
    const applyRepeatWood = (t, gc) => {
      if (!t) return

      setupTextureRepeat(
        t,
        1 / gc.multiplierVertical,
        1 / gc.multiplierHorizontal
      )

      t.center.set(0.5, 0.5)
      t.rotation = Math.PI / 2 // jak będzie w złą stronę: -Math.PI / 2
      t.needsUpdate = true
    }

    newClone.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry || !obj.material) return

      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      const touchesTarget = mats.some(isTargetMaterial)
      if (!touchesTarget) return

      // 1) Geometria: klon + skalowanie UV
      // ✅ tylko dla wood skalujemy UV pod wymiar; dla plastiku NIE skalujemy (stretch 1:1)
      const clonedGeometry = obj.geometry.clone()
      const scaledGeometry = cloneAndScaleUV(
        clonedGeometry,
        1, 1, 1,
        isWoodGlobal ? (gate.width / 100) : 1,
        isWoodGlobal ? (gate.height / 100) : 1
      )
      obj.geometry = scaledGeometry

      // 2) Materiały: klon i modyfikacja tylko tych "Material"
      const newMats = mats.map((mat) => {
        if (!isTargetMaterial(mat)) return mat

        const m = mat.clone()

        if (textures && textures.gates_sectional_color) {
          const gc = textures.gates_sectional_color
          const isWood = gc.materialType === 'wooden'

          if (isWood) {
            // WOOD: diffuse + normal + roughness
            m.map = gc.textureDiff || null
            m.normalMap = gc.textureNormal || gc.textureNormalMap || null
            m.roughnessMap = gc.textureRoughness || gc.textureRoughnessMap || null

            m.metalnessMap = null
            m.metalness = 0
            m.roughness = typeof gc.roughness === 'number' ? gc.roughness : 0.6
            if (m.color) m.color = new THREE.Color(gc.hex)
            ;[m.map, m.normalMap, m.roughnessMap].forEach((t) => applyRepeatWood(t, gc))
          } else {
            // ✅ PLASTIC_DEFAULT: base + normal (bez roughnessMap / metalnessMap)
            const pd = textures?.plastic_default || null
            const basePlastic = pd?.textureDiff || pd?.texture || null
            const normalPlastic =
              pd?.textureNormalness || pd?.textureNormal || pd?.textureNormalMap || null

            m.map = basePlastic ? (basePlastic.clone?.() ?? basePlastic) : null
            m.normalMap = normalPlastic ? (normalPlastic.clone?.() ?? normalPlastic) : null

            m.roughnessMap = null
            m.metalnessMap = null

            // stretch 1:1
            if (m.map) {
              setupTextureRepeat(m.map, 1, 1)
              m.map.offset.set(0, 0)
            }
            if (m.normalMap) {
              setupTextureRepeat(m.normalMap, 1, 1)
              m.normalMap.offset.set(0, 0)
              // opcjonalnie: m.normalScale?.set(0.5, 0.5)
            }

            // plastik: bez metalu
            m.metalness = 0
            m.roughness = 0.6
            if (m.color) {
              m.color.set(gc.hex)
              m.color.lerp(new THREE.Color('#ffffff'), 0.01) // 0.05–0.25
            }
          }
          m.needsUpdate = true
        }

        return m
      })

      obj.material = Array.isArray(obj.material) ? newMats : newMats[0]
    })

    cloned.current = newClone
    group.current.clear()
    group.current.add(newClone)
  }, [scene, gate.width, gate.height, props.length, props.width, textures])

  // Extract nodes and materials (offset UV na teksturach)
  const { nodes, materials } = useMemo(() => {
    const nodes = {}
    const materials = {}

    if (cloned.current) {
      const isWoodGlobal = textures?.gates_sectional_color?.materialType === 'wooden'

      // ✅ offsety robimy tylko dla wood; dla plastiku nie ruszamy
      const offsetStep = 0.17
      let i = 0

      cloned.current.traverse((obj) => {
        if (!obj.isMesh || !obj.material) return

        obj.material = obj.material.clone()

        // jeśli to nie wood -> nie ruszamy offsetów
        if (!isWoodGlobal) return

        const bumpOffset = (key) => {
          const t = obj.material[key]
          if (!t) return
          obj.material[key] = t.clone()
          obj.material[key].needsUpdate = true

          // ✅ po obrocie drewna: offset w X (lewo->prawo), nie w Y
          obj.material[key].offset.x += i * offsetStep
        }

        bumpOffset('map')
        bumpOffset('normalMap')
        bumpOffset('roughnessMap')

        i++
      })
    }

    return { nodes, materials }
  }, [cloned.current, textures])

  const [scaledAnimations, setScaledAnimations] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const mixerRef = useRef(null)

  useImperativeHandle(ref, () => group.current)

  // Clone animations
  useEffect(() => {
    if (!group.current) return

    const newScaledAnimations = animations.map((clip) => {
      const clonedTracks = clip.tracks.map((track) => {
        if (track.name.endsWith(".position")) {
          const times = Array.from(track.times)
          const values = Array.from(track.values)
          for (let i = 0; i < values.length; i += 3) {
            values[i] = values[i]
            values[i + 1] = values[i + 1]
            values[i + 2] = values[i + 2]
          }
          return new THREE.VectorKeyframeTrack(track.name, times, values)
        }
        return track.clone()
      })
      return new THREE.AnimationClip(clip.name, -1, clonedTracks)
    })

    setScaledAnimations(newScaledAnimations)
  }, [animations, gate.width, gate.height, gate.position, props.width, props.length, textures])

  // Mixer setup
  useEffect(() => {
    if (!group.current) return

    if (mixerRef.current) {
      mixerRef.current.stopAllAction()
      mixerRef.current.uncacheRoot(group.current)
    }

    mixerRef.current = new THREE.AnimationMixer(group.current)

    scaledAnimations.forEach(clip => {
      const action = mixerRef.current.clipAction(clip)
      action.clampWhenFinished = true
      action.enabled = true
      action.paused = true
    })
  }, [scaledAnimations])

  // Animation loop
  useEffect(() => {
    let frameId
    const animate = () => {
      mixerRef.current?.update(1 / 60)
      frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [])

  // Reset animation when position changes
  useEffect(() => {
    scaledAnimations.forEach(clip => {
      const action = mixerRef.current.clipAction(clip)
      action.reset()
      action.stop()
    })

    setIsOpen(false)
    isOpenRef.current = false
    isAnimatingRef.current = false
  }, [gate.position])

  // Bounding box updater
  useGateBoundingDataChecker({
    nodes,
    gate,
    onBoundingDataChanged: (newBoundingData) => {
      if (isAnimatingRef.current) return

      const box = new THREE.Box3().setFromObject(group.current)
      const size = new THREE.Vector3()
      box.getSize(size)

      const updateData = {
        boundingData: { box, size }
      }

      if (!isOpen) {
        const [x = 0, y = 0, z = 0] = [(-props.length / 100) / 2 + 1, 0, gate.position.z]
        const adjustedY = y + gate.height / 100 / 2

        updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
        updateData.positionAdjusted = [x, adjustedY, z]
        onUpdate(updateData)
      }
    },
    group,
  })

  // Click to open/close
  const handleClick = () => {
    if (!mixerRef.current || scaledAnimations.length === 0) return

    const clip = scaledAnimations[0]
    const action = mixerRef.current.clipAction(clip)
    action.reset()
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.enabled = true
    action.paused = false

    isAnimatingRef.current = true

    const onFinished = () => {
      isAnimatingRef.current = false
      mixerRef.current.removeEventListener('finished', onFinished)
    }

    mixerRef.current.addEventListener('finished', onFinished)

    if (!isOpen) {
      action.timeScale = 1
      action.play()
    } else {
      action.time = clip.duration
      action.timeScale = -1
      action.play()
    }

    setIsOpen(!isOpen)
  }

  return (
    <group
      ref={group}
      wall={'front_wall'}
      dispose={null}
      position={[gate.position.x, gate.position.y, gate.position.z]}
      castShadow={true}
      scale={[gate.height / 100, gate.height / 100, gate.width / 100]}
      onClick={handleClick}
    />
  )
})

export default BramaSegmentowa

useGLTF.preload('/models/gates/Brama-segmentowa.glb')
